import React, { useEffect, useMemo, useRef } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  LegendItem,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { isEqual } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  zoomPlugin,
);

ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

interface TargetGraphProps {
  yAxisMin?: number;
  yAxisMax?: number;
}

function TargetGraph({ yAxisMin, yAxisMax }: TargetGraphProps) {
  const chartRef = useRef<ChartJS<"line"> | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const tvEnergyData = useSelector(
    (state: RootState) => state.tvEnergy.data,
    isEqual,
  );
  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );
  const currentLap = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status?.currentLap,
  );
  const finalLaps = useSelector(
    (state: RootState) => state.aks.v2.timing?.session?.status?.finalLaps,
  );

  const chartData = useMemo(() => {
    if (!tvEnergyData || Object.keys(tvEnergyData).length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const datasets = Object.keys(tvEnergyData).map((driverKey, _) => {
      const driverData = tvEnergyData[driverKey];
      const driverName = driverData.driverName || driverKey;

      const driverInfoEntry = driverInfo[driverKey as keyof typeof driverInfo];
      const color = driverInfoEntry?.driverTeamColour || "white";
      const lineStyle = driverInfoEntry?.driverLineStyle || "solid";
      const borderDash = lineStyle === "solid" ? [] : [10, 5];

      const dataPoints: { x: number; y: number }[] = [];

      if (driverData.estimatedTargetByLap) {
        Object.entries(driverData.estimatedTargetByLap).forEach(
          ([lapNumber, estimatedTarget]) => {
            const lapNum = Number(lapNumber);
            if (!isNaN(estimatedTarget) && !isNaN(lapNum)) {
              dataPoints.push({
                x: lapNum,
                y: estimatedTarget,
              });
            }
          },
        );
      }

      return {
        label: driverName,
        data: dataPoints,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 3,
        pointRadius: 3,
        pointHoverRadius: 6,
        fill: false,
        borderDash: borderDash,
      };
    });

    // Log target by lap data to console
    const targetByLap: Record<string, Record<number, number>> = {};
    Object.keys(tvEnergyData).forEach((driverKey) => {
      const driverData = tvEnergyData[driverKey];
      if (driverData.estimatedTargetByLap) {
        targetByLap[driverData.driverName || driverKey] =
          driverData.estimatedTargetByLap;
      }
    });
    console.log("Target by lap:", JSON.stringify(targetByLap, null, 2));

    return { datasets };
  }, [tvEnergyData, driverInfo]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let animationFrame: number | undefined;
    const resizeChart = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(() => {
        chartRef.current?.resize();
      });
    };

    resizeChart();

    const resizeObserver = new ResizeObserver(resizeChart);
    resizeObserver.observe(container);
    window.addEventListener("resize", resizeChart);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      resizeObserver.disconnect();
      window.removeEventListener("resize", resizeChart);
    };
  }, []);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#000000ff",
          usePointStyle: true,
          generateLabels: (chart: ChartJS): LegendItem[] => {
            const datasets = chart.data.datasets;

            return datasets.map((dataset, index) => ({
              text: dataset.label ?? "",
              strokeStyle: dataset.borderColor as string,
              lineWidth: (dataset.borderWidth as number) ?? 3,
              lineDash: (dataset as { borderDash?: number[] }).borderDash ?? [],
              hidden: !chart.isDatasetVisible(index),
              datasetIndex: index,
              pointStyle: "line" as const,
            }));
          },
        },
      },
      title: {
        display: true,
        text: "Estimated Target by Lap",
        color: "#000000ff",
        font: {
          size: 18,
          weight: "bold" as const,
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        min: 0,
        max:
          currentLap && finalLaps
            ? currentLap === finalLaps + 1
              ? finalLaps
              : currentLap
            : undefined,
        title: {
          display: true,
          text: "End of Lap Number",
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
          stepSize: 1,
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      y: {
        min: yAxisMin,
        max: yAxisMax,
        title: {
          display: true,
          text: "Estimated Target (kWh)",
          color: "#000000ff",
        },
        ticks: {
          color: "#000000ff",
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
    },
    interaction: {
      mode: "point" as const,
      axis: "x" as const,
      intersect: false,
    },
    elements: {
      point: {
        backgroundColor: "#000000ff",
        borderWidth: 2,
      },
    },
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <Line ref={chartRef} data={chartData} options={chartOptions} />
    </div>
  );
}

export default TargetGraph;
